import React from "react"
import PropTypes from "prop-types"
import { useLocation } from "@reach/router"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function SEO({ description, lang, meta, title, image, article }) {
  const location = useLocation()
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const canonical = `${site.siteMetadata.siteUrl}${location.pathname.replace(/\/$/, "")}`
  
  // Default image for social sharing
  const defaultImage = `${site.siteMetadata.siteUrl}/src/images/feature_bar_bckgrd.png`
  const socialImage = image || defaultImage

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        {
          rel: "canonical",
          href: canonical,
        },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        // Open Graph / Facebook
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: article ? `article` : `website`,
        },
        {
          property: `og:url`,
          content: canonical,
        },
        {
          property: `og:site_name`,
          content: site.siteMetadata.title,
        },
        // Twitter
        {
          name: `twitter:card`,
          content: socialImage ? `summary_large_image` : `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        // Conditionally add image meta tags
        ...(socialImage ? [
          {
            property: `og:image`,
            content: socialImage,
          },
          {
            property: `og:image:width`,
            content: `1200`,
          },
          {
            property: `og:image:height`,
            content: `630`,
          },
          {
            property: `og:image:type`,
            content: `image/jpeg`,
          },
          {
            name: `twitter:image`,
            content: socialImage,
          },
          {
            name: `twitter:image:alt`,
            content: `${title} - ${metaDescription}`,
          },
        ] : []),
      ].concat(meta)}
    />
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  image: null,
  article: false,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  article: PropTypes.bool,
}

export default SEO
